import { useEffect } from 'react'
import { format } from 'date-fns'

import usePrivateApi from '../../../../hooks/usePrivateApi'
import Button from '../../../../components/Button'
import Label from '../../../../components/Label'

const WhiteLabelCheckout = ({ whiteLabel, setWhiteLabel, dealer }) => {
  const [
    { res, success, error, isLoading },
    { sendRequest, resetSuccess, resetError },
  ] = usePrivateApi()

  const handleEnableCheckout = async () => {
    resetError()
    sendRequest({
      url: `/admin/dealers/${dealer.id}/white-label/checkout`,
      method: 'post',
    })
  }

  const handleCheckStatus = async () => {
    resetError()
    sendRequest({
      url: `/admin/dealers/${dealer.id}/white-label/checkout`,
    })
  }

  useEffect(() => {
    if (success) {
      setWhiteLabel((prevState) => ({
        ...prevState,
        stripe: { ...prevState.stripe, ...res.data.stripe },
      }))

      resetSuccess()
    }
  }, [setWhiteLabel, success, res, resetSuccess])

  return (
    <>
      <div
        className={
          'border-b border-gray-200 px-6 py-6 space-y-4 sm:space-y-0 sm:flex sm:space-x-8 sm:items-start flex-1'
        }
      >
        <div className="max-w-sm flex-1">
          <h2 className="text-lg font-semibold tracking-tight">
            White-label Checkout
          </h2>
          <div className="text-gray-600 text-sm space-y-2 max-w-prose">
            <p>
              Allows master dealers to sell credits to subdealers with an
              automatic checkout, powered by Stripe.
            </p>
            <p>
              Enabling this feature will create a Stripe Connected Account and
              generate a link for the master dealer in their dealer settings.
              The master dealer will need to follow this link and complete the
              onboarding process directly with Stripe.
            </p>
          </div>
        </div>
        <div className="flex-1">
          {!whiteLabel.stripe.accountId && (
            <div className="mt-2">
              <p className="block text-sm font-medium leading-5 text-black">
                Enable checkout
              </p>
              <p className="text-sm text-gray-700 mt-1">
                You must notify the master dealer once checkout has been
                enabled.
              </p>
              <p className="text-sm text-gray-700 mt-2">
                Please note that billing is not handled by the portal. You will
                need to invoice and collect payment from the dealer manually for
                use of this feature.
              </p>
              <Button
                color="primary"
                disabled={isLoading}
                onClick={handleEnableCheckout}
                size="sm"
                className="mt-4"
              >
                {isLoading ? 'Enabling...' : 'Enable checkout'}
              </Button>
            </div>
          )}
          {whiteLabel.stripe.accountId && (
            <>
              <div className="flex items-center space-x-4 mt-2">
                <p className="text-sm font-semibold tracking-tight text-gray-900">
                  Checkout status:
                </p>
                <Label
                  color={!whiteLabel.stripe.requiresAction ? 'green' : 'pink'}
                >
                  {!whiteLabel.stripe.requiresAction
                    ? 'Enabled'
                    : 'Requires action'}
                </Label>
              </div>
              {error && (
                <p className="text-sm text-red-600 mt-2">
                  Error whilst checking status
                </p>
              )}
              <p className="text-sm text-gray-700 mt-2">
                Last checked:{' '}
                {
                  //prettier-ignore
                  whiteLabel.stripe.lastStatusUpdate
                    ? format(
                      new Date(whiteLabel.stripe.lastStatusUpdate),
                      "dd MMM yyyy 'at' h:mma"
                    )
                    : 'Never'
                }
              </p>
              {whiteLabel.stripe.requiresAction && (
                <p className="text-sm text-gray-700 mt-2">
                  White-label checkout is enabled, but Stripe requires the
                  dealer to sort something out directly with them. This will be
                  the case when checkout is first enabled, or whenever Stripe
                  place new requirements on the dealer that are not yet
                  complete.
                </p>
              )}
              <Button
                color="white"
                disabled={isLoading}
                onClick={handleCheckStatus}
                size="sm"
                className="mt-3"
              >
                {isLoading ? 'Checking...' : 'Check status'}
              </Button>
            </>
          )}
        </div>
      </div>
    </>
  )
}

export default WhiteLabelCheckout
